import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LucideAngularModule,
  Sparkles,
  ArrowRight,
  Eye,
  EyeOff,
  Mail,
  Lock,
  Star,
} from 'lucide-angular';
import { AuthService } from '../../common/auth/auth.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule, RouterLink],
  templateUrl: './login.html',
  styleUrl: './login.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isLoading$ = new BehaviorSubject<boolean>(false);
  protected readonly errorMessage$ = new BehaviorSubject<string>('');
  protected readonly showPassword = signal(false);

  protected readonly loginForm: FormGroup;

  protected readonly icons = {
    Sparkles,
    ArrowRight,
    Eye,
    EyeOff,
    Mail,
    Lock,
    Star,
  };

  protected readonly redirectUrl = '/dashboard';

  constructor() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
    });
  }

  protected togglePasswordVisibility(): void {
    this.showPassword.update((v) => !v);
  }

  protected onSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading$.next(true);
    this.errorMessage$.next('');

    const { email, password } = this.loginForm.value;
    const request = { email, password };

    this.authService
      .signIn(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLoading$.next(false);
          this.router.navigate([this.redirectUrl]);
        },
        error: (error) => {
          this.isLoading$.next(false);
          this.errorMessage$.next(error.error?.message || 'Invalid email or password. Please try again.');
        },
      });
  }

  protected signInWithGoogle(): void {
    this.authService.signInWithGoogle(this.redirectUrl);
  }

  protected signInWithMicrosoft(): void {
    this.authService.signInWithMicrosoft(this.redirectUrl);
  }
}

